import type { Room, SceneDocument, Vec2, Vec3, Wall } from "@calcplan/domain";

export interface ArchitecturalFloorPlacement {
  roomId: string;
  primaryWallId: string;
  secondaryWallId: string | null;
  physicalCenterMm: Vec3;
  rotationDeg: Vec3;
  reasons: string[];
}

interface ArchitecturalFloorPlacementInput {
  scene: SceneDocument;
  primaryWallId: string;
  secondaryWallId?: string | null;
  roomId?: string | null;
  sizeMm: { width: number; height: number; depth: number };
  clearanceMm: number;
}

const EPSILON_MM = 2;

function distance(left: Vec2, right: Vec2): number {
  return Math.hypot(left[0] - right[0], left[1] - right[1]);
}

function average(points: readonly Vec2[]): Vec2 {
  if (!points.length) return [0, 0];
  const sum = points.reduce<Vec2>((value, point) => [value[0] + point[0], value[1] + point[1]], [0, 0]);
  return [sum[0] / points.length, sum[1] / points.length];
}

function roomContainsWall(room: Room, wall: Wall): boolean {
  return room.boundary.some((point) => distance(point, wall.startMm) <= EPSILON_MM)
    && room.boundary.some((point) => distance(point, wall.endMm) <= EPSILON_MM);
}

function roomForWall(scene: SceneDocument, wall: Wall, roomId?: string | null): Room {
  if (roomId) {
    const room = scene.rooms.find((candidate) => candidate.id === roomId);
    if (!room) throw new Error(`Ambiente ${roomId} não foi encontrado.`);
    if (!roomContainsWall(room, wall)) throw new Error(`A parede ${wall.id} não pertence ao ambiente ${roomId}.`);
    return room;
  }
  const candidates = scene.rooms.filter((room) => room.valid && roomContainsWall(room, wall));
  if (candidates.length === 1) return candidates[0]!;
  if (!candidates.length && scene.rooms.length === 1) return scene.rooms[0]!;
  throw new Error(`Informe roomId para posicionar o módulo contra a parede ${wall.id}.`);
}

function wallFrame(wall: Wall, room: Room): { tangent: Vec2; inward: Vec2; midpoint: Vec2 } {
  const dx = wall.endMm[0] - wall.startMm[0];
  const dz = wall.endMm[1] - wall.startMm[1];
  const length = Math.hypot(dx, dz);
  if (length <= EPSILON_MM) throw new Error(`A parede ${wall.id} não possui comprimento utilizável.`);
  const tangent: Vec2 = [dx / length, dz / length];
  const midpoint: Vec2 = [(wall.startMm[0] + wall.endMm[0]) / 2, (wall.startMm[1] + wall.endMm[1]) / 2];
  const roomCenter = average(room.boundary);
  const left: Vec2 = [-tangent[1], tangent[0]];
  const toRoom: Vec2 = [roomCenter[0] - midpoint[0], roomCenter[1] - midpoint[1]];
  const inward = left[0] * toRoom[0] + left[1] * toRoom[1] >= 0
    ? left
    : [-left[0], -left[1]] as Vec2;
  return { tangent, inward, midpoint };
}

function sharedEndpoint(left: Wall, right: Wall): Vec2 | null {
  for (const first of [left.startMm, left.endMm]) {
    for (const second of [right.startMm, right.endMm]) {
      if (distance(first, second) <= EPSILON_MM) return [(first[0] + second[0]) / 2, (first[1] + second[1]) / 2];
    }
  }
  return null;
}

/**
 * Resolves a floor module against an architectural wall, optionally locked to
 * the corner made by a second wall. Unlike semantic furniture placement, wall
 * and room ids are first-class architectural references here.
 */
export function resolveArchitecturalFloorPlacement(
  input: ArchitecturalFloorPlacementInput,
): ArchitecturalFloorPlacement {
  const primary = input.scene.walls.find((wall) => wall.id === input.primaryWallId);
  if (!primary) throw new Error(`Parede ${input.primaryWallId} não foi encontrada.`);
  const room = roomForWall(input.scene, primary, input.roomId);
  const primaryFrame = wallFrame(primary, room);
  const primaryOffset = primary.thicknessMm / 2 + input.sizeMm.depth / 2 + input.clearanceMm;
  const yawRad = Math.atan2(primaryFrame.inward[0], primaryFrame.inward[1]);

  let centerX = primaryFrame.midpoint[0] + primaryFrame.inward[0] * primaryOffset;
  let centerZ = primaryFrame.midpoint[1] + primaryFrame.inward[1] * primaryOffset;
  let secondaryWallId: string | null = null;
  const reasons = [`fundo alinhado à parede ${primary.id}`, `folga de ${input.clearanceMm} mm da parede principal`];

  if (input.secondaryWallId) {
    const secondary = input.scene.walls.find((wall) => wall.id === input.secondaryWallId);
    if (!secondary) throw new Error(`Parede ${input.secondaryWallId} não foi encontrada.`);
    if (secondary.id === primary.id) throw new Error("O canto precisa de duas paredes diferentes.");
    if (!roomContainsWall(room, secondary)) throw new Error(`A parede ${secondary.id} não pertence ao ambiente ${room.id}.`);
    const corner = sharedEndpoint(primary, secondary);
    if (!corner) throw new Error(`As paredes ${primary.id} e ${secondary.id} não formam um canto.`);
    const secondaryFrame = wallFrame(secondary, room);
    const orthogonality = Math.abs(
      primaryFrame.inward[0] * secondaryFrame.inward[0]
      + primaryFrame.inward[1] * secondaryFrame.inward[1],
    );
    if (orthogonality > 0.15) throw new Error(`As paredes ${primary.id} e ${secondary.id} não formam um canto aproximadamente reto.`);
    const secondaryOffset = secondary.thicknessMm / 2 + input.sizeMm.width / 2 + input.clearanceMm;
    centerX = corner[0]
      + primaryFrame.inward[0] * primaryOffset
      + secondaryFrame.inward[0] * secondaryOffset;
    centerZ = corner[1]
      + primaryFrame.inward[1] * primaryOffset
      + secondaryFrame.inward[1] * secondaryOffset;
    secondaryWallId = secondary.id;
    reasons.push(`lateral alinhada à parede ${secondary.id}`, `folga de ${input.clearanceMm} mm da parede lateral`);
  }

  return {
    roomId: room.id,
    primaryWallId: primary.id,
    secondaryWallId,
    physicalCenterMm: [centerX, input.sizeMm.height / 2, centerZ],
    rotationDeg: [0, yawRad * 180 / Math.PI, 0],
    reasons,
  };
}
